import moment from 'moment'
import { canCompleteAction } from 'common/util/unlinkedActions'

/**
 * API expects integer minutes_section_id or null. Values may be number or string (forms / JSON);
 * -1 or '-1' means "no section" and maps to null.
 */
export function normalizeMinutesSectionId(
    minutesSectionId: unknown,
    minutes_section_id?: unknown
): number | null {
    const raw = minutesSectionId ?? minutes_section_id
    if (raw === null || raw === undefined || raw === '') {
        return null
    }
    if (raw === -1 || raw === '-1') {
        return null
    }
    const n =
        typeof raw === 'number' && Number.isFinite(raw)
            ? Math.trunc(raw)
            : parseInt(String(raw).trim(), 10)
    if (Number.isNaN(n) || n === -1) {
        return null
    }
    return n
}

//:unsent, :sent, :completed, :overdue
const templateObject = (): any => {
    return {
        data: {
            type: 'action_items',
            attributes: {
                minutes_section_id: null,
                sort_order: -1,
                text: '',
                note: null,
                notification_status: 'unsent',
                completion_status: 'incomplete',
                due_date: null,
                assignees: null
            }
        }
    }
}

const templateActionReorder = (): any => {
    return {
        data: {
            type: 'action_items',
            id: -1,
            attributes: {
                minutes_section_id: -1,
                sort_order: -1
            }
        }
    }
}

const getAssignees = (item) => {
    if (!item.assignees || !item.assignees.length) {
        return null
    }
    const formatted = item.assignees.map((assignee) => {
        let id = typeof assignee.value === 'number' ? parseInt(assignee.value, 10) : null
        if (assignee.id) {
            id = assignee.id
        }
        return {
            id: id,
            email: assignee.email || null,
            display_name: assignee.text,
            self_manage: assignee.self_manage
        }
    })
    return formatted
}

export const serialize = (item, isAdmin: boolean = false) => {
    const requestData = templateObject()
    requestData.data.attributes.note = item.notes

    /*
     * Only send completion_status when the caller may change it: admin,
     * self-managed action, or an unlinked action (feature on + committee no
     * longer visible). See canCompleteAction for the shared rule.
     */
    requestData.data.attributes.completion_status = canCompleteAction(item, isAdmin)
        ? item.completionStatus
        : undefined

    if (isAdmin) {
        const hasDueDate = item.dueDate && moment(item.dueDate).isValid()
        const sectionId = normalizeMinutesSectionId(item.minutesSectionId, item.minutes_section_id)

        Object.assign(requestData.data.attributes, {
            text: item.text,
            notification_status: item.notificationStatus,
            sort_order: item.sortOrder,
            minutes_section_id: sectionId,
            minutesId: item.minutesId,
            due_date:
                item.dueDate && moment(item.dueDate).isValid()
                    ? moment(item.dueDate).toISOString()
                    : item.dueDate ?? null,
            assignees: getAssignees(item),
            completion_status: item.completionStatus
        })

        if (hasDueDate) {
            if (item.hasOwnProperty('reminders')) {
                requestData.data.attributes.reminders = item.reminders || []
            }
            // Only serialize when the client explicitly set this (checkbox); do not infer from reminders.
            if (item.hasOwnProperty('remember_reminder_settings')) {
                requestData.data.attributes.remember_reminder_settings =
                    item.remember_reminder_settings === true
            }
        } else if (item.id) {
            requestData.data.attributes.remember_reminder_settings = false
            requestData.data.attributes.reminders = []
        }
    } else {
        // completion_status is already set above via canCompleteAction(item, isAdmin).
        Object.assign(requestData.data.attributes, {
            text: undefined,
            notification_status: undefined,
            sort_order: undefined,
            minutes_section_id: undefined,
            minutesId: undefined,
            due_date: undefined,
            assignees: undefined
        })
    }

    return JSON.stringify(requestData)
}

export const serializeActions = (items) => {
    const requestData: { data: any[] } = { data: [] }
    items.forEach((item) => {
        const actionItem: { data: any } = templateActionReorder()
        actionItem.data.id = item.id
        actionItem.data.attributes.sort_order = item.sortOrder
        actionItem.data.attributes.minutes_section_id = normalizeMinutesSectionId(
            item.minutesSectionId,
            item.minutes_section_id
        )

        requestData.data.push(actionItem.data)
    })

    return JSON.stringify(requestData)
}
